import { useCallback } from 'react';
import { RefObject } from 'react';
import { PopoverPosition } from '../types';

export function useApplyPopoverPosition({
  triggerRef,
  contentWrapperRef,
  contentRef,
  currentPosition,
}: {
  triggerRef: RefObject<HTMLElement>;
  contentWrapperRef: RefObject<HTMLElement>;
  contentRef: RefObject<HTMLElement>;
  currentPosition: PopoverPosition;
}) {
  return useCallback(() => {
    if (
      !triggerRef.current ||
      !contentWrapperRef.current ||
      !contentRef.current
    )
      return;

    // Use requestAnimationFrame for smoother positioning
    requestAnimationFrame(() => {
      if (typeof window === 'undefined') return;
      if (
        !triggerRef.current ||
        !contentWrapperRef.current ||
        !contentRef.current
      )
        return;

      const buttonRect = triggerRef.current.getBoundingClientRect();
      const popoverRect = contentRef.current.getBoundingClientRect();

      // Get scroll position
      const scrollTop =
        window.pageYOffset || document.documentElement.scrollTop;
      const viewportWidth = document.documentElement.clientWidth;

      let left, top;

      switch (currentPosition) {
        case 'bottom':
          left = buttonRect.left + buttonRect.width / 2 - popoverRect.width / 2;
          top = buttonRect.bottom + 8;
          break;

        case 'left':
          left = buttonRect.left - popoverRect.width - 8;
          top = buttonRect.top + buttonRect.height / 2 - popoverRect.height / 2;
          break;

        case 'right':
          left = buttonRect.right + 8;
          top = buttonRect.top + buttonRect.height / 2 - popoverRect.height / 2;
          break;

        case 'top':
        default:
          left = buttonRect.left + buttonRect.width / 2 - popoverRect.width / 2;
          top = buttonRect.top - popoverRect.height - 8;
          break;
      }

      // Convert to absolute position
      top += scrollTop;

      // Apply position
      contentWrapperRef.current.style.position = 'absolute';

      // Ensure left position stays within viewport
      const leftOffset = Math.max(
        10,
        Math.min(left, viewportWidth - popoverRect.width - 10),
      );

      contentWrapperRef.current.style.left = `${leftOffset}px`;
      contentWrapperRef.current.style.top = `${top}px`;
    });
  }, [currentPosition, triggerRef, contentWrapperRef, contentRef]);
}
